import { pickBoolean, pickEnum, pickNumber, pickString } from "@/lib/search-params";

export const ADMIN_OFFER_STATUSES = ["active", "needs_review", "paused", "expired"] as const;
export const ADMIN_OFFER_RELATIONSHIP_STATUSES = [
  "unmatched",
  "needs_review",
  "active",
  "paused",
  "rejected",
] as const;
export const ADMIN_OFFER_PRICE_MOVEMENTS = ["up", "down", "flat", "unknown"] as const;
export const ADMIN_OFFER_SORTS = [
  "created_desc",
  "updated_desc",
  "checked_desc",
  "price_asc",
  "price_desc",
  "drop_desc",
  "drop_asc",
] as const;
export const DEFAULT_ADMIN_OFFER_SORT = "checked_desc";
export const DEFAULT_ADMIN_OFFER_LIMIT = 100;

const MAX_ADMIN_OFFER_LIMIT = 500;

export type AdminOfferListSearch = {
  dealMovement?: (typeof ADMIN_OFFER_PRICE_MOVEMENTS)[number];
  limit: number;
  needsAction?: boolean;
  offset: number;
  outOfStock?: boolean;
  providerId?: string;
  q?: string;
  relationshipStatus?: (typeof ADMIN_OFFER_RELATIONSHIP_STATUSES)[number];
  sort: (typeof ADMIN_OFFER_SORTS)[number];
  status?: (typeof ADMIN_OFFER_STATUSES)[number];
};

export type AdminOfferDetailSearch = AdminOfferListSearch & {
  position?: number;
};

export function validateAdminOfferListSearch(raw: Record<string, unknown>): AdminOfferListSearch {
  return {
    dealMovement: pickEnum(raw.dealMovement, ADMIN_OFFER_PRICE_MOVEMENTS),
    limit: clampAdminOfferLimit(pickNumber(raw.limit)),
    needsAction: pickBoolean(raw.needsAction) ?? false,
    offset: clampAdminOfferOffset(pickNumber(raw.offset)),
    outOfStock: pickBoolean(raw.outOfStock) ? true : undefined,
    providerId: pickString(raw.providerId),
    q: pickString(raw.q),
    relationshipStatus: pickEnum(raw.relationshipStatus, ADMIN_OFFER_RELATIONSHIP_STATUSES),
    sort: pickEnum(raw.sort, ADMIN_OFFER_SORTS) ?? DEFAULT_ADMIN_OFFER_SORT,
    status: pickEnum(raw.status, ADMIN_OFFER_STATUSES),
  };
}

export function validateAdminOfferDetailSearch(
  raw: Record<string, unknown>,
): AdminOfferDetailSearch {
  const position = pickNumber(raw.position);

  return {
    ...validateAdminOfferListSearch(raw),
    position: position === undefined ? undefined : clampAdminOfferOffset(position),
  };
}

export function adminOfferListQueryInput(search: AdminOfferListSearch) {
  return {
    dealMovement: search.dealMovement,
    limit: search.limit,
    needsAction: search.needsAction,
    offset: search.offset,
    outOfStock: search.outOfStock,
    providerId: search.providerId,
    query: search.q,
    relationshipStatus: search.relationshipStatus,
    sort: search.sort,
    status: search.status,
  };
}

export function adminOfferPositionQueryInput(search: AdminOfferListSearch, position: number) {
  const { limit: _limit, offset: _offset, ...scope } = adminOfferListQueryInput(search);

  return { ...scope, position };
}

function clampAdminOfferLimit(value: number | undefined) {
  if (!value) return DEFAULT_ADMIN_OFFER_LIMIT;
  return Math.min(Math.max(Math.trunc(value), 1), MAX_ADMIN_OFFER_LIMIT);
}

function clampAdminOfferOffset(value: number | undefined) {
  if (!value) return 0;
  return Math.max(Math.trunc(value), 0);
}
